import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { SkeletonLine, EmptyState } from "./state-parts";
import type { PrimitiveState } from "@/lib/types";

export interface KpiCardProps {
  label: string;
  value?: string;
  /** Direction-agnostic delta description, e.g. "14% vs last Tuesday". */
  delta?: string;
  deltaDirection?: "up" | "down" | "flat";
  icon?: ReactNode;
  state?: Extract<PrimitiveState, "loading" | "empty" | "populated">;
  className?: string;
}

/**
 * KPI / status card. Per docs/COMPONENT_LIBRARY.md this must always show a
 * meaningful delta/trend next to the headline number — never a bare number
 * with no context for whether it's good or bad.
 */
export function KpiCard({ label, value, delta, deltaDirection = "flat", icon, state = "populated", className }: KpiCardProps) {
  const deltaColor =
    deltaDirection === "up" ? "text-status-success" : deltaDirection === "down" ? "text-status-danger" : "text-text-secondary";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-card border border-border-subtle bg-surface-primary p-4 shadow-[var(--shadow-subtle)]",
        className,
      )}
      data-state={state}
    >
      {icon ? (
        <div className="absolute end-3.5 top-3.5 flex h-8 w-8 items-center justify-center rounded-control bg-status-success-bg text-brand-primary">
          {icon}
        </div>
      ) : null}
      <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary">{label}</p>

      {state === "loading" ? (
        <div className="mt-2 flex flex-col gap-2" aria-label={`${label} loading`}>
          <SkeletonLine className="h-7 w-20" />
          <SkeletonLine className="h-3 w-28" />
        </div>
      ) : state === "empty" ? (
        <div className="mt-1">
          <EmptyState title="No data yet" description="This metric will appear once service begins." />
        </div>
      ) : (
        <>
          <p className="tabular-figures mt-1 text-2xl font-extrabold text-text-primary sm:text-[27px]">{value}</p>
          {delta ? (
            <p className={cn("mt-1.5 flex items-center gap-1 text-[11px] font-semibold", deltaColor)}>
              {deltaDirection === "up" ? (
                <TrendingUp aria-hidden="true" size={12} />
              ) : deltaDirection === "down" ? (
                <TrendingDown aria-hidden="true" size={12} />
              ) : null}
              {delta}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
